import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Mic,
  User,
  Bot,
  Settings,
  Clock,
  Calendar,
  Activity,
  FileText,
  Wrench,
  Check,
  AlertTriangle,
  Lightbulb,
  Star,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneForwarded,
  Users,
} from "lucide-react";
import { sessionApi, type Session } from "../services/sessionApi";
import { transcriptApi, type Transcript } from "../services/transcriptApi";

import { MarkdownRenderer } from "../components/MarkdownRenderer";
import { useQuery } from "@tanstack/react-query";
import { SuccessIndicator } from "@/components/analysis/SuccessIndicator";
import { QualityBar } from "@/components/analysis/QualityBar";
import { StatusBadge } from "@/components/analysis/StatusBadge";
import { ComplianceCheck } from "@/components/analysis/ComplianceCheck";
import { MetricDisplay } from "@/components/analysis/MetricDisplay";
import { ScoreBadge } from "@/components/analysis/ScoreBadge";
import { useNavigate } from "react-router-dom";

export function SessionDetailsPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const {
    data: session,
    isLoading: isSessionLoading,
    isFetching: isSessionFetching,
    error: sessionError,
  } = useQuery<Session, unknown>({
    queryKey: ["session", sessionId],
    queryFn: () => sessionApi.getSession(sessionId as string),
    enabled: !!sessionId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const {
    data: transcripts,
    isLoading: isTranscriptsLoading,
    isFetching: isTranscriptsFetching,
    error: transcriptsError,
  } = useQuery<Transcript[], unknown>({
    queryKey: ["sessionTranscripts", sessionId],
    queryFn: () => transcriptApi.getTranscriptsBySession(sessionId as string),
    enabled: !!sessionId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const isInitialLoading =
    (isSessionLoading || isTranscriptsLoading) &&
    !(isSessionFetching || isTranscriptsFetching);
  const combinedError =
    (sessionError as Error) || (transcriptsError as Error) || null;

  const getSessionTypeIcon = (type: string) => {
    switch (type) {
      case "call":
        return <Phone className="h-5 w-5" />;
      case "web_voice":
        return <Mic className="h-5 w-5" />;
      case "web_chat":
        return <MessageCircle className="h-5 w-5" />;
      default:
        return <Activity className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      completed:
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    };

    return (
      <Badge
        className={
          variants[status as keyof typeof variants] ||
          "bg-gray-100 text-gray-800"
        }
      >
        {status}
      </Badge>
    );
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case "user":
        return <User className="h-6 w-6" />;
      case "model":
        return <Bot className="h-6 w-6" />;
      case "system":
        return <Settings className="h-6 w-6" />;
      default:
        return <FileText className="h-6 w-6" />;
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case "user":
        return "text-blue-600 bg-blue-50";
      case "model":
        return "text-green-600 bg-green-50";
      case "system":
        return "text-gray-600 bg-gray-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "tool":
        return <Wrench className="h-4 w-4" />;
      case "audio":
        return <Mic className="h-4 w-4" />;
      case "text":
        return <MessageCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (milliseconds?: number) => {
    if (!milliseconds) return "-";
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
  };

  const renderTranscriptContent = (transcript: Transcript) => {
    return <MarkdownRenderer>{transcript.content}</MarkdownRenderer>;
  };

  const toFriendlyNumber = (number: string) => {
    // Remove any non-digit characters except the leading +
    const cleaned = number.replace(/[^\d+]/g, "");

    // Handle E.164 format (starts with + and country code)
    if (cleaned.startsWith("+1") && cleaned.length === 12) {
      // US/Canada number: +1XXXXXXXXXX
      const digits = cleaned.slice(2); // Remove +1
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (cleaned.startsWith("+") && cleaned.length > 10) {
      // International number - just return the original for now
      return number;
    } else if (cleaned.length === 10) {
      // Already a 10-digit US number without country code
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
      // 11-digit US number starting with 1
      const digits = cleaned.slice(1);
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    // Return original if format is not recognized
    return number;
  };

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p>Loading session transcript...</p>
      </div>
    );
  }

  if (!sessionId || !session) {
    return (
      <div className="flex items-center justify-center h-64">
        <p>Session not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Session Details</h1>
            <p className="text-gray-600 mt-1">{session.company.name}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-sm text-gray-500">
            Session ID: {session.id.substring(0, 8)}...
          </div>
        </div>
      </div>

      {combinedError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {combinedError.message || "Failed to load session data"}
        </div>
      )}

      {/* Session Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            {getSessionTypeIcon(session.type)}
            <span>Session Details</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="text-sm text-gray-500 mb-1">Status</div>
              {getStatusBadge(session.status)}
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Type</div>
              <div className="flex items-center space-x-2">
                {getSessionTypeIcon(session.type)}
                <span>{session.type.replace("_", " ")}</span>
              </div>
            </div>
            {session.direction && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Direction</div>
                <div className="flex items-center space-x-2">
                  {session.direction === "outbound" ? (
                    <PhoneOutgoing className="h-4 w-4 text-blue-600" />
                  ) : (
                    <PhoneIncoming className="h-4 w-4 text-green-600" />
                  )}
                  <span className="capitalize">{session.direction}</span>
                </div>
              </div>
            )}
            <div>
              <div className="text-sm text-gray-500 mb-1">Duration</div>
              <div className="flex items-center space-x-1">
                <Clock className="h-4 w-4 text-gray-400" />
                <span>{formatDuration(session.duration)}</span>
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Created</div>
              <div className="flex items-center space-x-1">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>{formatDateTime(session.createdAt)}</span>
              </div>
            </div>
            {session.model && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Model</div>
                <span className="font-mono text-sm">{session.model}</span>
              </div>
            )}
            {session.voice && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Voice</div>
                <span>{session.voice}</span>
              </div>
            )}
            {session.fromNumber && (
              <div>
                <div className="text-sm text-gray-500 mb-1">From Number</div>
                <span>{toFriendlyNumber(session.fromNumber)}</span>
              </div>
            )}
            {session.toNumber && (
              <div>
                <div className="text-sm text-gray-500 mb-1">To Number</div>
                <span>{session.toNumber.friendlyName}</span>
              </div>
            )}
            {session.toolsUsed && session.toolsUsed.length > 0 && (
              <div className="md:col-span-2">
                <div className="text-sm text-gray-500 mb-1">Tools Used</div>
                <div className="flex flex-wrap gap-1">
                  {session.toolsUsed.map((tool, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tool}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Outbound Call Details */}
      {session.outboundCall && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <PhoneOutgoing className="h-5 w-5 text-blue-600" />
              <span>Outbound Call Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <div className="text-sm text-gray-500 mb-1">Initiated By</div>
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="font-medium">
                      {session.outboundCall.user.firstName}{" "}
                      {session.outboundCall.user.lastName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {session.outboundCall.user.email}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Called Number</div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="font-mono">
                    {toFriendlyNumber(session.outboundCall.toNumber)}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Call Status</div>
                <Badge className="capitalize">
                  {session.outboundCall.status.replace("_", " ")}
                </Badge>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Call SID</div>
                <span className="font-mono text-xs text-gray-600">
                  {session.outboundCall.callSid}
                </span>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Initiated At</div>
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">
                    {formatDateTime(session.outboundCall.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Call Transfer Details */}
      {session.callTransfers && session.callTransfers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <PhoneForwarded className="h-5 w-5 text-orange-600" />
              <span>Call Transfer History</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {session.callTransfers.map((transfer, index) => (
                <div
                  key={transfer.id}
                  className="border rounded-lg p-4 bg-orange-50 dark:bg-orange-950"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold flex items-center space-x-2">
                      <span>Transfer #{index + 1}</span>
                      <Badge
                        className={
                          transfer.status === "connected"
                            ? "bg-green-100 text-green-800"
                            : transfer.status === "failed" ||
                              transfer.status === "agent_no_answer"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }
                      >
                        {transfer.status.replace("_", " ")}
                      </Badge>
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Transferred To
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="font-mono">
                          {toFriendlyNumber(transfer.transferTo)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Reason</div>
                      <p className="text-sm">{transfer.reason}</p>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Conference SID
                      </div>
                      <span className="font-mono text-xs text-gray-600">
                        {transfer.conferenceSid}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Initiated At
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="text-sm">
                          {formatDateTime(transfer.initiatedAt)}
                        </span>
                      </div>
                    </div>
                    {transfer.connectedAt && (
                      <div>
                        <div className="text-sm text-gray-500 mb-1">
                          Connected At
                        </div>
                        <div className="flex items-center space-x-1">
                          <Check className="h-3 w-3 text-green-600" />
                          <span className="text-sm">
                            {formatDateTime(transfer.connectedAt)}
                          </span>
                        </div>
                      </div>
                    )}
                    {transfer.completedAt && (
                      <div>
                        <div className="text-sm text-gray-500 mb-1">
                          Completed At
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span className="text-sm">
                            {formatDateTime(transfer.completedAt)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Analysis Overview */}
      {session.analysis && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-5 w-5" />
                <span>AI Analysis Overview</span>
              </div>
              <ScoreBadge score={session.analysis.overallScore} />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Core Success Indicators */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Core Success Metrics
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <SuccessIndicator
                  label="Session Completed"
                  value={session.analysis.sessionCompleted}
                />
                <SuccessIndicator
                  label="Objective Achieved"
                  value={session.analysis.objectiveAchieved}
                />
                <SuccessIndicator
                  label="Customer Satisfied"
                  value={session.analysis.customerSatisfied}
                />
                <SuccessIndicator
                  label="Tool Calls Successful"
                  value={session.analysis.toolCallsSuccessful}
                />
                <SuccessIndicator
                  label="Order Placed"
                  value={session.analysis.orderPlaced}
                />
                <SuccessIndicator
                  label="Follow-ups Appropriate"
                  value={session.analysis.followUpQuestionsAppropriate}
                />
              </div>
            </div>

            {/* AI Summary */}
            {session.analysis.summary && (
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      Summary
                    </h4>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      {session.analysis.summary}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Efficiency & Technical Performance */}
      {session.analysis && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Conversation Quality */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Star className="h-5 w-5" />
                <span>Conversation Quality Metrics</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <QualityBar
                  label="Response Appropriateness"
                  score={
                    session.analysis.conversationQuality.responseAppropriateness
                  }
                />
                <QualityBar
                  label="Conversation Flow"
                  score={session.analysis.conversationQuality.conversationFlow}
                />
                <QualityBar
                  label="Information Accuracy"
                  score={
                    session.analysis.conversationQuality.informationAccuracy
                  }
                />
                <QualityBar
                  label="Clarity of Communication"
                  score={
                    session.analysis.conversationQuality.clarityOfCommunication
                  }
                />
                <QualityBar
                  label="Professional Tone"
                  score={session.analysis.conversationQuality.professionalTone}
                />
                <QualityBar
                  label="Empathy & Rapport"
                  score={session.analysis.conversationQuality.empathyAndRapport}
                />
              </div>
            </CardContent>
          </Card>
          {/* Efficiency */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Efficiency Metrics</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Time to Resolution
                  </span>
                  <span className="font-medium">
                    {session.analysis.efficiency?.timeToResolution ?? "N/A"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Call Duration</span>
                  <StatusBadge
                    value={session.analysis.efficiency?.callDurationAppropriate}
                    trueLabel="Appropriate"
                    falseLabel="Inappropriate"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Unnecessary Repetition
                  </span>
                  <StatusBadge
                    value={!session.analysis.efficiency?.unnecessaryRepetition}
                    trueLabel="None"
                    falseLabel="Detected"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Dead Air</span>
                  <StatusBadge
                    value={!session.analysis.efficiency?.deadAirDetected}
                    trueLabel="None"
                    falseLabel="Detected"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Technical Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5" />
                <span>Technical Performance</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Tool Usage</span>
                  <StatusBadge
                    value={
                      session.analysis.technicalPerformance.toolUsageAccurate
                    }
                    trueLabel="Accurate"
                    falseLabel="Issues"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Audio Quality</span>
                  <StatusBadge
                    value={
                      !session.analysis.technicalPerformance.audioQualityIssues
                    }
                    trueLabel="Good"
                    falseLabel="Issues"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">System Errors</span>
                  <StatusBadge
                    value={!session.analysis.technicalPerformance.systemErrors}
                    trueLabel="None"
                    falseLabel="Detected"
                  />
                </div>
                {session.analysis.technicalPerformance.toolsCalled.length >
                  0 && (
                  <div>
                    <span className="text-sm text-gray-600 block mb-2">
                      Function Calls Used:
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {session.analysis.technicalPerformance.toolsCalled.map(
                        (fn, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {fn}
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Order Details - Only if order was placed */}
      {session.analysis?.orderDetails && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Order Performance</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <MetricDisplay
                label="Order Completeness"
                value={session.analysis.orderDetails.orderCompleteness}
              />
              <MetricDisplay
                label="Price Accuracy"
                value={session.analysis.orderDetails.priceAccuracy}
              />
              <div className="space-y-1">
                <span className="text-sm text-gray-500">Order Confirmed</span>
                <div className="pt-1">
                  <StatusBadge
                    value={session.analysis.orderDetails.orderConfirmed}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-sm text-gray-500">Details Captured</span>
                <div className="pt-1">
                  <StatusBadge
                    value={
                      session.analysis.orderDetails.allRequiredDetailsCaptured
                    }
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Compliance & Best Practices */}
      {session.analysis && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Check className="h-5 w-5" />
              <span>Compliance & Best Practices</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ComplianceCheck
                label="Information Gathered"
                passed={
                  session.analysis.complianceAndBestPractices
                    .informationGathered
                }
              />
              <ComplianceCheck
                label="Unknown Questions Handled"
                passed={
                  session.analysis.complianceAndBestPractices
                    .unknownQuestionsHandled
                }
              />
              <ComplianceCheck
                label="Privacy Compliant"
                passed={
                  session.analysis.complianceAndBestPractices.privacyCompliant
                }
              />
              <ComplianceCheck
                label="Proper Call Closing"
                passed={
                  session.analysis.complianceAndBestPractices.properCallClosing
                }
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Strengths, Weaknesses & Recommendations */}
      {session.analysis && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Strengths */}
          <Card>
            <CardHeader>
              <CardTitle className="text-green-700 dark:text-green-400 flex items-center space-x-2">
                <Check className="h-5 w-5" />
                <span>Strengths</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {session.analysis.strengths.length > 0 ? (
                <ul className="space-y-2">
                  {session.analysis.strengths.map((strength, i) => (
                    <li key={i} className="text-sm flex items-start">
                      <Check className="h-4 w-4 text-green-600 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">No strengths identified</p>
              )}
            </CardContent>
          </Card>

          {/* Weaknesses */}
          <Card>
            <CardHeader>
              <CardTitle className="text-orange-700 dark:text-orange-400 flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5" />
                <span>Weaknesses</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {session.analysis.weaknesses.length > 0 ? (
                <ul className="space-y-2">
                  {session.analysis.weaknesses.map((weakness, i) => (
                    <li key={i} className="text-sm flex items-start">
                      <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{weakness}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">
                  No weaknesses identified
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-700 dark:text-blue-400 flex items-center space-x-2">
                <Lightbulb className="h-5 w-5" />
                <span>Recommendations</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {session.analysis.recommendations.length > 0 ? (
                <ul className="space-y-2">
                  {session.analysis.recommendations.map((rec, i) => (
                    <li key={i} className="text-sm flex items-start">
                      <Lightbulb className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">
                  No recommendations provided
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Transcripts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Transcript ({transcripts?.length ?? 0} entries)</span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transcripts?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No transcripts found for this session.
            </div>
          ) : (
            <div className="space-y-3">
              {transcripts?.map((transcript, index) => (
                <div
                  key={transcript.id}
                  className="border border-secondary rounded-lg p-2"
                >
                  {/* Transcript Header */}
                  <div className="flex items-center space-x-6">
                    <div
                      className={`p-2 rounded-full ${getSourceColor(
                        transcript.source
                      )}`}
                    >
                      {getSourceIcon(transcript.source)}
                    </div>
                    <div className="flex w-full flex-col">
                      <div className="flex justify-between w-full">
                        <div className="flex items-center space-x-2">
                          {/* <span className="font-medium capitalize">
                                {transcript.source.toLowerCase()}
                              </span> */}
                          <div className="flex items-center space-x-1">
                            {getTypeIcon(transcript.type)}
                            <span className="text-xs">
                              {/* {transcript.type} */}
                              {formatDateTime(transcript.createdAt)}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 font-mono">
                          #{index + 1}
                        </div>
                      </div>
                      {/* Transcript Content */}
                      <div className="">
                        {renderTranscriptContent(transcript)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
